import { computed } from "vue";
import type { Ref, ComputedRef } from "vue";
import type { SkillWithMetrics } from "./useSkillMetrics";

export enum SkillTier {
  Expert = "expert",
  Proficient = "proficient",
  Familiar = "familiar",
}

export function getTier(expertise: number): SkillTier {
  if (expertise >= 4) {
    return SkillTier.Expert;
  }
  if (expertise >= 2) {
    return SkillTier.Proficient;
  }
  return SkillTier.Familiar;
}

export type SkillsByTier = Record<SkillTier, SkillWithMetrics[]>;

export type SkillsByCategory = Record<string, SkillWithMetrics[]>;

export interface SkillCategoriesResult {
  byTier: ComputedRef<SkillsByTier>;
  byCategory: ComputedRef<SkillsByCategory>;
  categories: ComputedRef<string[]>;
  tiers: SkillTier[];
}

const TIER_ORDER: SkillTier[] = [
  SkillTier.Expert,
  SkillTier.Proficient,
  SkillTier.Familiar,
];

export function useSkillCategories(
  skills: Ref<SkillWithMetrics[]> | ComputedRef<SkillWithMetrics[]>
): SkillCategoriesResult {
  const byTier = computed<SkillsByTier>(() => {
    const result: SkillsByTier = {
      [SkillTier.Expert]: [],
      [SkillTier.Proficient]: [],
      [SkillTier.Familiar]: [],
    };

    for (const skill of skills.value) {
      const tier = getTier(skill.expertise);
      result[tier].push(skill);
    }

    // Within each tier: sort by expertise DESC, then alphabetically
    for (const tier of TIER_ORDER) {
      result[tier].sort((a, b) => {
        if (b.expertise !== a.expertise) {
          return b.expertise - a.expertise;
        }
        return a.name.localeCompare(b.name);
      });
    }

    return result;
  });

  const byCategory = computed<SkillsByCategory>(() => {
    const result: SkillsByCategory = {};

    for (const skill of skills.value) {
      for (const category of skill.categories) {
        if (!result[category]) {
          result[category] = [];
        }
        result[category].push(skill);
      }
    }

    // Sort within each category by expertise DESC, then alphabetically
    for (const category of Object.keys(result)) {
      result[category].sort((a, b) => {
        if (b.expertise !== a.expertise) {
          return b.expertise - a.expertise;
        }
        return a.name.localeCompare(b.name);
      });
    }

    return result;
  });

  const categories = computed<string[]>(() =>
    Object.keys(byCategory.value).sort()
  );

  return { byTier, byCategory, categories, tiers: TIER_ORDER };
}
